/**
 * NeoCoreVerificacionesBancariasPage — bandeja del operador para verificar
 * cuentas bancarias de asociados via micro-depósitos.
 *
 * Flujo del operador:
 *   1. Ve cuentas pendientes_inicio → click "Iniciar verificación"
 *      → sistema genera monto random Q0.07-Q0.99
 *      → operador hace el depósito manualmente desde la banca de la coop
 *      → click "Marcar como enviado" (puede agregar # boleta)
 *   2. Espera que el asociado confirme desde la app
 *   3. Si el asociado falla 3 veces → puede reiniciar o rechazar
 */
import { useCallback, useEffect, useMemo, useState } from 'react'
import {
  ArrowsClockwise,
  Bank,
  CheckCircle,
  Hourglass,
  MagnifyingGlass,
  Warning,
  XCircle,
} from '@phosphor-icons/react'
import { supabase } from '@/lib/supabase'
import { VerificarPorComprobanteModal } from '@/components/VerificarPorComprobanteModal'

type Estado =
  | 'pendiente_inicio'
  | 'verificacion_enviada'
  | 'verificada'
  | 'fallida'
  | 'rechazada'

type OcrExtraido = {
  banco: string | null
  numero_cuenta: string | null
  tipo_cuenta: 'AHORROS' | 'MONETARIA' | null
  titular_nombre: string | null
  confianza: 'alta' | 'media' | 'baja' | null
}

type CuentaRow = {
  id: string
  asociado_id: string
  asociado_nombre: string
  asociado_no: string
  asociado_dpi: string
  banco: string
  tipo_cuenta: string
  numero_cuenta: string
  titular_nombre: string
  estado: Estado
  monto_verificacion: number | null
  intentos_restantes: number
  numero_boleta: string | null
  motivo_rechazo: string | null
  metodo_verificacion: string | null
  comprobante_aporte_url: string | null
  comprobante_titularidad_path: string | null
  ocr_extraido: OcrExtraido | null
  created_at: string
  verificacion_iniciada_at: string | null
  verificada_at: string | null
}

/** Cuenta en un estado donde todavía tiene sentido verificar por comprobante. */
const puedeVerificarPorComprobante = (estado: Estado) =>
  estado === 'pendiente_inicio' || estado === 'verificacion_enviada' || estado === 'fallida'

const metodoLabel = (metodo: string | null): string | null => {
  if (!metodo) return null
  if (metodo === 'comprobante_asociacion') return 'por comprobante'
  if (metodo === 'deposito_espejo') return 'depósito espejo'
  if (metodo === 'microdeposito') return 'microdepósito'
  return metodo
}

const ESTADO_BADGE: Record<Estado, { label: string; color: string; bg: string }> = {
  pendiente_inicio:      { label: 'Pendiente de inicio',   color: 'var(--warning, #d97706)', bg: 'rgba(217,119,6,0.10)' },
  verificacion_enviada:  { label: 'Esperando asociado',    color: 'var(--brand-700)',         bg: 'var(--brand-50)' },
  verificada:            { label: 'Verificada',            color: 'var(--ok-ink, #0f8a57)',   bg: 'rgba(15,138,87,0.10)' },
  fallida:               { label: 'Fallida (3 intentos)',  color: 'var(--danger, #a32d2d)',   bg: 'rgba(163,45,45,0.10)' },
  rechazada:             { label: 'Rechazada',             color: 'var(--ink-soft, #4b5563)', bg: 'rgba(75,85,99,0.08)' },
}

const fmtQ = (n: number) => 'Q ' + Number(n).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 4 })

const fmtFechaHora = (iso: string | null) => {
  if (!iso) return '—'
  return new Date(iso).toLocaleString('es-GT', {
    day: '2-digit', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
    timeZone: 'America/Guatemala',
  })
}

// ── Comparación declarado vs OCR ────────────────────────────
type MatchNivel = 'coincide' | 'revisar' | 'no_coincide' | 'sin_dato'

const MATCH_BADGE: Record<MatchNivel, { label: string; color: string; bg: string }> = {
  coincide:    { label: 'Coincide',     color: 'var(--ok-ink, #0f8a57)',   bg: 'rgba(15,138,87,0.10)' },
  revisar:     { label: 'Revisar',      color: 'var(--warning, #d97706)',  bg: 'rgba(217,119,6,0.10)' },
  no_coincide: { label: 'No coincide',  color: 'var(--danger, #a32d2d)',   bg: 'rgba(163,45,45,0.10)' },
  sin_dato:    { label: 'Sin dato OCR', color: 'var(--ink-soft, #4b5563)', bg: 'rgba(75,85,99,0.08)' },
}

const CONFIANZA_BADGE: Record<'alta' | 'media' | 'baja', { label: string; color: string; bg: string }> = {
  alta:  { label: 'Confianza alta',  color: 'var(--ok-ink, #0f8a57)',  bg: 'rgba(15,138,87,0.10)' },
  media: { label: 'Confianza media', color: 'var(--warning, #d97706)', bg: 'rgba(217,119,6,0.10)' },
  baja:  { label: 'Confianza baja',  color: 'var(--danger, #a32d2d)',  bg: 'rgba(163,45,45,0.10)' },
}

/** Normaliza texto: mayúsculas, sin tildes, espacios colapsados. */
const normText = (s: string | null | undefined): string =>
  (s ?? '')
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toUpperCase()
    .replace(/\s+/g, ' ')
    .trim()

const onlyDigits = (s: string | null | undefined): string => (s ?? '').replace(/\D/g, '')

const matchNumero = (declarado: string, ocr: string | null): MatchNivel => {
  const o = onlyDigits(ocr)
  if (!o) return 'sin_dato'
  return onlyDigits(declarado) === o ? 'coincide' : 'no_coincide'
}

const matchTipo = (declarado: string, ocr: string | null): MatchNivel => {
  if (!ocr) return 'sin_dato'
  return normText(declarado) === normText(ocr) ? 'coincide' : 'no_coincide'
}

const matchTitular = (declarado: string, ocr: string | null): MatchNivel => {
  const o = normText(ocr)
  if (!o) return 'sin_dato'
  const d = normText(declarado)
  if (!d) return 'revisar'
  if (d === o || d.includes(o) || o.includes(d)) return 'coincide'
  // Coincidencia parcial: comparten algún token largo → revisar; si no, distinto.
  const dTokens = d.split(' ').filter((t) => t.length >= 3)
  const oTokens = new Set(o.split(' ').filter((t) => t.length >= 3))
  const compartidos = dTokens.filter((t) => oTokens.has(t)).length
  return compartidos > 0 ? 'revisar' : 'no_coincide'
}

const matchBanco = (declarado: string, ocr: string | null): MatchNivel => {
  const o = normText(ocr)
  if (!o) return 'sin_dato'
  const d = normText(declarado)
  if (d === o || d.includes(o) || o.includes(d)) return 'coincide'
  return 'revisar'
}

export function NeoCoreVerificacionesBancariasPage() {
  const [rows, setRows] = useState<CuentaRow[]>([])
  const [loading, setLoading] = useState(true)
  const [filtro, setFiltro] = useState<Estado | 'todas'>('pendiente_inicio')
  const [search, setSearch] = useState('')
  const [drawer, setDrawer] = useState<CuentaRow | null>(null)
  const [comprobanteModal, setComprobanteModal] = useState<CuentaRow | null>(null)

  const load = useCallback(async () => {
    setLoading(true)
    const { data } = await supabase
      .from('cuenta_bancaria_asociado')
      .select(`
        id, asociado_id, banco, tipo_cuenta, numero_cuenta, titular_nombre,
        estado, monto_verificacion, intentos_restantes, numero_boleta_microdeposito,
        motivo_rechazo, metodo_verificacion, comprobante_titularidad_path, ocr_extraido,
        created_at, verificacion_iniciada_at, verificada_at,
        asociado:asociado_id (no_asociado, nombre_completo, dpi, comprobante_aporte_url)
      `)
      .eq('activa', true)
      .order('created_at', { ascending: false })
      .limit(200)
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const mapped: CuentaRow[] = (data ?? []).map((r: any) => ({
      id: r.id,
      asociado_id: r.asociado_id,
      asociado_nombre: r.asociado?.nombre_completo ?? '—',
      asociado_no: r.asociado?.no_asociado ?? '—',
      asociado_dpi: r.asociado?.dpi ?? '—',
      banco: r.banco,
      tipo_cuenta: r.tipo_cuenta,
      numero_cuenta: r.numero_cuenta,
      titular_nombre: r.titular_nombre,
      estado: r.estado,
      monto_verificacion: r.monto_verificacion,
      intentos_restantes: r.intentos_restantes,
      numero_boleta: r.numero_boleta_microdeposito,
      motivo_rechazo: r.motivo_rechazo,
      metodo_verificacion: r.metodo_verificacion ?? null,
      comprobante_aporte_url: r.asociado?.comprobante_aporte_url ?? null,
      comprobante_titularidad_path: r.comprobante_titularidad_path ?? null,
      ocr_extraido: r.ocr_extraido ?? null,
      created_at: r.created_at,
      verificacion_iniciada_at: r.verificacion_iniciada_at,
      verificada_at: r.verificada_at,
    }))
    setRows(mapped)
    setLoading(false)
  }, [])

  useEffect(() => { void load() }, [load])

  const counts = useMemo(() => ({
    todas: rows.length,
    pendiente_inicio: rows.filter((r) => r.estado === 'pendiente_inicio').length,
    verificacion_enviada: rows.filter((r) => r.estado === 'verificacion_enviada').length,
    verificada: rows.filter((r) => r.estado === 'verificada').length,
    fallida: rows.filter((r) => r.estado === 'fallida').length,
    rechazada: rows.filter((r) => r.estado === 'rechazada').length,
  }), [rows])

  const filtered = useMemo(() => {
    let list = filtro === 'todas' ? rows : rows.filter((r) => r.estado === filtro)
    const q = search.trim().toLowerCase()
    if (q) {
      list = list.filter((r) =>
        r.asociado_nombre.toLowerCase().includes(q) ||
        r.asociado_no.toLowerCase().includes(q) ||
        r.numero_cuenta.includes(q) ||
        r.banco.toLowerCase().includes(q),
      )
    }
    return list
  }, [rows, filtro, search])

  return (
    <div style={{ padding: '20px 24px', maxWidth: 1300, margin: '0 auto' }}>
      <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', marginBottom: 18, gap: 16 }}>
        <div>
          <h1 style={{ margin: 0, fontSize: 22, fontWeight: 800, letterSpacing: '-0.01em' }}>
            Verificaciones bancarias
          </h1>
          <p style={{ margin: '4px 0 0', fontSize: 13, color: 'var(--muted-foreground)' }}>
            Cuentas bancarias pendientes de verificar contra comprobante de asociación o depósito espejo.
          </p>
        </div>
        <button onClick={() => void load()} className="btn btn-ghost" style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
          <ArrowsClockwise size={13} weight="bold" /> Refrescar
        </button>
      </div>

      {/* Filtros + búsqueda */}
      <div style={{
        background: 'var(--surface)', border: '1px solid var(--hairline)',
        borderRadius: 12, padding: '12px 14px', marginBottom: 12,
        display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap',
      }}>
        <div style={{ display: 'flex', gap: 4 }}>
          {([
            ['pendiente_inicio', 'Pendientes', counts.pendiente_inicio],
            ['verificacion_enviada', 'Enviadas', counts.verificacion_enviada],
            ['verificada', 'Verificadas', counts.verificada],
            ['fallida', 'Fallidas', counts.fallida],
            ['rechazada', 'Rechazadas', counts.rechazada],
            ['todas', 'Todas', counts.todas],
          ] as Array<[Estado | 'todas', string, number]>).map(([key, label, n]) => {
            const active = filtro === key
            return (
              <button
                key={key}
                onClick={() => setFiltro(key)}
                style={{
                  all: 'unset', cursor: 'pointer',
                  display: 'inline-flex', alignItems: 'center', gap: 6,
                  padding: '6px 12px', borderRadius: 99,
                  background: active ? 'var(--brand-50)' : 'transparent',
                  color: active ? 'var(--brand-700)' : 'var(--ink-soft)',
                  fontSize: 12, fontWeight: 700,
                  border: '1px solid', borderColor: active ? 'var(--brand-300, transparent)' : 'transparent',
                }}
              >
                {label}
                <span style={{
                  fontSize: 10, fontWeight: 800, padding: '1px 6px', borderRadius: 99,
                  background: active ? 'var(--brand)' : 'var(--n-100)',
                  color: active ? '#fff' : 'var(--muted-foreground)',
                  fontFeatureSettings: '"tnum"',
                }}>{n}</span>
              </button>
            )
          })}
        </div>
        <div style={{ flex: 1 }} />
        <div style={{
          display: 'flex', alignItems: 'center', gap: 6,
          background: 'var(--bg)', borderRadius: 8,
          padding: '6px 10px', border: '1px solid var(--hairline)',
          minWidth: 240,
        }}>
          <MagnifyingGlass size={14} weight="bold" color="var(--muted-foreground)" />
          <input
            type="text" value={search} onChange={(e) => setSearch(e.target.value)}
            placeholder="Asociado, banco, # cuenta…"
            style={{
              flex: 1, border: 0, background: 'transparent',
              outline: 'none', fontSize: 13, fontFamily: 'inherit',
              color: 'var(--ink)',
            }}
          />
        </div>
      </div>

      {/* Tabla */}
      <div style={{
        background: 'var(--surface)', border: '1px solid var(--hairline)',
        borderRadius: 12, overflow: 'hidden',
      }}>
        {loading ? (
          <div style={{ padding: 40, textAlign: 'center', color: 'var(--muted-foreground)' }}>Cargando…</div>
        ) : filtered.length === 0 ? (
          <div style={{ padding: 40, textAlign: 'center', color: 'var(--muted-foreground)' }}>
            <Bank size={28} weight="duotone" /><br />
            Sin cuentas con este filtro.
          </div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ borderBottom: '1px solid var(--hairline)', background: 'var(--bg)' }}>
                <th style={th}>Asociado</th>
                <th style={th}>Banco</th>
                <th style={th}>Cuenta</th>
                <th style={th}>Titular</th>
                <th style={th}>Estado</th>
                <th style={th}>Acción</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((r) => {
                const badge = ESTADO_BADGE[r.estado]
                return (
                  <tr key={r.id} style={{ borderBottom: '1px solid var(--line-soft)' }}>
                    <td style={td}>
                      <div style={{ fontWeight: 600 }}>{r.asociado_nombre}</div>
                      <div style={{ fontSize: 11, color: 'var(--muted-foreground)' }}>{r.asociado_no}</div>
                    </td>
                    <td style={td}>
                      <div style={{ fontWeight: 600 }}>{r.banco}</div>
                      <div style={{ fontSize: 11, color: 'var(--muted-foreground)' }}>{r.tipo_cuenta}</div>
                    </td>
                    <td style={{ ...td, fontFeatureSettings: '"tnum"' }}>{r.numero_cuenta}</td>
                    <td style={td}>{r.titular_nombre}</td>
                    <td style={td}>
                      <span style={{
                        fontSize: 10, fontWeight: 800, padding: '3px 10px', borderRadius: 99,
                        background: badge.bg, color: badge.color,
                        textTransform: 'uppercase', letterSpacing: 0.5,
                      }}>
                        {badge.label}
                      </span>
                      {r.estado === 'verificada' && metodoLabel(r.metodo_verificacion) && (
                        <span style={{
                          marginLeft: 6,
                          fontSize: 10, fontWeight: 700, padding: '3px 8px', borderRadius: 99,
                          background: 'var(--n-100)', color: 'var(--ink-soft, #4b5563)',
                          whiteSpace: 'nowrap',
                        }}>
                          {metodoLabel(r.metodo_verificacion)}
                        </span>
                      )}
                    </td>
                    <td style={td}>
                      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                        {puedeVerificarPorComprobante(r.estado) && (
                          <button
                            onClick={() => setComprobanteModal(r)}
                            className="btn btn-primary"
                            style={{ fontSize: 12, padding: '5px 12px', whiteSpace: 'nowrap' }}
                          >
                            Verificar por comprobante
                          </button>
                        )}
                        <button
                          onClick={() => setDrawer(r)}
                          className="btn btn-ghost"
                          style={{ fontSize: 12, padding: '5px 12px' }}
                        >
                          Ver
                        </button>
                      </div>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        )}
      </div>

      {drawer && (
        <VerificacionDrawer
          cuenta={drawer}
          onClose={() => setDrawer(null)}
          onChange={() => { void load(); setDrawer(null) }}
          onVerificarPorComprobante={() => {
            setComprobanteModal(drawer)
            setDrawer(null)
          }}
        />
      )}

      {comprobanteModal && (
        <VerificarPorComprobanteModal
          open
          cuenta={comprobanteModal}
          onClose={() => setComprobanteModal(null)}
          onSuccess={() => { void load() }}
        />
      )}
    </div>
  )
}

const th: React.CSSProperties = {
  textAlign: 'left', padding: '10px 14px',
  fontSize: 10, fontWeight: 800, color: 'var(--muted-foreground)',
  textTransform: 'uppercase', letterSpacing: 1.2,
}
const td: React.CSSProperties = { padding: '12px 14px' }

// ============================================================
// Drawer de detalle / acciones
// ============================================================

function VerificacionDrawer({
  cuenta, onClose, onChange, onVerificarPorComprobante,
}: {
  cuenta: CuentaRow
  onClose: () => void
  onChange: () => void
  onVerificarPorComprobante: () => void
}) {
  const [submitting, setSubmitting] = useState(false)
  const [montoGenerado] = useState<number | null>(cuenta.monto_verificacion)
  const [numeroBoleta, setNumeroBoleta] = useState(cuenta.numero_boleta ?? '')
  const [motivoRechazo, setMotivoRechazo] = useState('')
  const [imgUrl, setImgUrl] = useState<string | null>(null)
  const [imgError, setImgError] = useState<string | null>(null)
  const [imgLoading, setImgLoading] = useState(false)

  // Imagen de titularidad: signed URL (TTL 5 min) del bucket privado 'cuentas'.
  const comprobantePath = cuenta.comprobante_titularidad_path
  useEffect(() => {
    if (!comprobantePath) return
    let cancelled = false
    setImgLoading(true)
    setImgError(null)
    supabase.storage
      .from('cuentas')
      .createSignedUrl(comprobantePath, 300)
      .then(({ data, error }) => {
        if (cancelled) return
        if (error || !data) {
          setImgError(error?.message ?? 'No pudimos generar la URL de la imagen.')
          setImgUrl(null)
        } else {
          setImgUrl(data.signedUrl)
        }
      })
      .catch((e) => {
        if (cancelled) return
        setImgError(e instanceof Error ? e.message : String(e))
      })
      .finally(() => {
        if (!cancelled) setImgLoading(false)
      })
    return () => { cancelled = true }
  }, [comprobantePath])

  // `iniciar` (generar monto de micro-depósito) retirado el 11-ago: el
  // método vigente es comprobante/espejo. marcarEnviado y reiniciar se
  // conservan solo para cerrar cuentas atoradas en estados legacy.
  const marcarEnviado = async () => {
    setSubmitting(true)
    const { error } = await supabase.rpc('confirmar_microdeposito_enviado_admin', {
      p_cuenta_id: cuenta.id,
      p_numero_boleta: numeroBoleta || null,
    })
    setSubmitting(false)
    if (error) { alert('Error: ' + error.message); return }
    onChange()
  }

  const reiniciar = async () => {
    if (!confirm('¿Reiniciar la verificación? Se generará un nuevo monto.')) return
    setSubmitting(true)
    const { error } = await supabase.rpc('reiniciar_verificacion_admin', { p_cuenta_id: cuenta.id })
    setSubmitting(false)
    if (error) { alert('Error: ' + error.message); return }
    onChange()
  }

  const verificar = async () => {
    setSubmitting(true)
    const { data, error } = await supabase.rpc('verificar_cuenta_bancaria_admin', {
      p_cuenta_id: cuenta.id,
      p_nota: null,
    })
    setSubmitting(false)
    if (error) { alert('Error: ' + error.message); return }
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const res = data as any
    if (res && res.ok === false) { alert('No se pudo verificar: ' + (res.error ?? 'error desconocido')); return }
    onChange()
  }

  const rechazar = async () => {
    if (!motivoRechazo.trim()) { alert('Ingresá un motivo'); return }
    setSubmitting(true)
    const { error } = await supabase.rpc('rechazar_cuenta_bancaria_admin', {
      p_cuenta_id: cuenta.id, p_motivo: motivoRechazo,
    })
    setSubmitting(false)
    if (error) { alert('Error: ' + error.message); return }
    onChange()
  }

  return (
    <>
      <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(15,18,25,0.40)', zIndex: 999 }} />
      <aside style={{
        position: 'fixed', top: 0, right: 0, bottom: 0,
        width: 'min(480px, 100%)', background: '#fff', zIndex: 1000,
        display: 'flex', flexDirection: 'column',
        boxShadow: '-12px 0 32px rgba(0,0,0,0.10)',
      }}>
        <div style={{ padding: '18px 24px', borderBottom: '1px solid var(--hairline)' }}>
          <h3 style={{ margin: 0, fontSize: 16, fontWeight: 800 }}>
            Verificación bancaria
          </h3>
          <p style={{ margin: '4px 0 0', fontSize: 12, color: 'var(--muted-foreground)' }}>
            {cuenta.asociado_nombre} · {cuenta.asociado_no}
          </p>
        </div>

        <div style={{ padding: '18px 24px', overflowY: 'auto', flex: 1 }}>
          <Info label="Banco" value={`${cuenta.banco} · ${cuenta.tipo_cuenta}`} />
          <Info label="Número de cuenta" value={cuenta.numero_cuenta} mono />
          <Info label="Titular" value={cuenta.titular_nombre} />
          <Info label="DPI" value={cuenta.asociado_dpi} mono />
          <Info label="Registrada" value={fmtFechaHora(cuenta.created_at)} />
          {cuenta.estado === 'verificacion_enviada' && (
            <>
              <Info label="Monto generado" value={montoGenerado ? fmtQ(montoGenerado) : '—'} highlight />
              <Info label="Iniciada" value={fmtFechaHora(cuenta.verificacion_iniciada_at)} />
              <Info label="Intentos restantes" value={String(cuenta.intentos_restantes)} />
            </>
          )}
          {cuenta.estado === 'verificada' && (
            <>
              <Info label="Verificada el" value={fmtFechaHora(cuenta.verificada_at)} highlight />
              {metodoLabel(cuenta.metodo_verificacion) && (
                <Info label="Método" value={metodoLabel(cuenta.metodo_verificacion)!} />
              )}
            </>
          )}
          {cuenta.estado === 'rechazada' && cuenta.motivo_rechazo && (
            <Info label="Motivo del rechazo" value={cuenta.motivo_rechazo} />
          )}

          {/* Imagen de titularidad + datos declarados para cotejar */}
          <div style={{ marginTop: 20 }}>
            <p style={{ margin: '0 0 4px', fontSize: 11, fontWeight: 800, color: 'var(--muted-foreground)', textTransform: 'uppercase', letterSpacing: 1.2 }}>
              Imagen de la cuenta (titularidad)
            </p>
            <p style={{ margin: '0 0 10px', fontSize: 12, color: 'var(--ink-soft)', lineHeight: 1.5 }}>
              Verificá que el número, tipo y titular coincidan con lo declarado.
            </p>

            {/* Datos declarados, para comparar contra la imagen */}
            <div style={{
              background: 'var(--bg)', border: '1px solid var(--hairline)',
              borderRadius: 10, padding: '10px 14px', marginBottom: 12,
            }}>
              <Info label="Banco" value={`${cuenta.banco} · ${cuenta.tipo_cuenta}`} />
              <Info label="Número declarado" value={cuenta.numero_cuenta} mono />
              <Info label="Titular declarado" value={cuenta.titular_nombre} highlight />
            </div>

            <OcrComparacion cuenta={cuenta} />

            {!comprobantePath ? (
              <div style={{
                border: '1px dashed var(--hairline)', borderRadius: 10,
                padding: '20px 16px', textAlign: 'center', fontSize: 12, color: 'var(--muted-foreground)',
              }}>
                El asociado no adjuntó imagen de titularidad.
              </div>
            ) : imgLoading ? (
              <div style={{ padding: 20, textAlign: 'center', fontSize: 12, color: 'var(--muted-foreground)' }}>Cargando imagen…</div>
            ) : imgError ? (
              <div style={{
                border: '1px solid var(--hairline)', borderRadius: 10,
                padding: '16px', fontSize: 12, color: 'var(--danger, #a32d2d)',
              }}>
                No se pudo cargar la imagen: {imgError}
              </div>
            ) : imgUrl ? (
              <a href={imgUrl} target="_blank" rel="noreferrer" style={{ display: 'block' }}>
                <img
                  src={imgUrl}
                  alt="Imagen de la cuenta (titularidad)"
                  style={{
                    width: '100%', borderRadius: 10, border: '1px solid var(--hairline)',
                    maxHeight: 340, objectFit: 'contain', background: 'var(--bg)',
                  }}
                />
              </a>
            ) : null}
          </div>

          {/* Acciones según estado */}
          <div style={{ marginTop: 24, display: 'flex', flexDirection: 'column', gap: 12 }}>
            {/* Micro-depósito retirado del flujo (11-ago): el método es
                comprobante de asociación / depósito espejo. El botón de
                abajo (Verificar por comprobante) es la acción principal. */}

            {cuenta.estado === 'verificacion_enviada' && montoGenerado && !cuenta.numero_boleta && (
              <>
                <div style={{
                  background: 'var(--brand-50)', border: '1px solid var(--brand-300, var(--brand-200))',
                  borderRadius: 10, padding: '14px 16px',
                }}>
                  <p style={{ margin: 0, fontSize: 11, fontWeight: 800, color: 'var(--brand-700)', textTransform: 'uppercase', letterSpacing: 1.5 }}>
                    Depositá exactamente
                  </p>
                  <p style={{ margin: '6px 0 0', fontSize: 28, fontWeight: 800, color: 'var(--brand)', fontFeatureSettings: '"tnum"' }}>
                    {fmtQ(montoGenerado)}
                  </p>
                  <p style={{ margin: '4px 0 0', fontSize: 12, color: 'var(--ink-soft)' }}>
                    A la cuenta {cuenta.banco} N° {cuenta.numero_cuenta}
                  </p>
                </div>
                <label style={{ fontSize: 11, fontWeight: 800, letterSpacing: 1.2, color: 'var(--muted-foreground)', textTransform: 'uppercase' }}>
                  N° boleta del depósito (opcional)
                </label>
                <input
                  type="text"
                  value={numeroBoleta}
                  onChange={(e) => setNumeroBoleta(e.target.value)}
                  placeholder="Ej. 123456"
                  style={{
                    padding: '10px 12px', border: '1px solid var(--hairline)',
                    borderRadius: 8, fontSize: 13, fontFamily: 'inherit',
                  }}
                />
                <button className="btn btn-primary" onClick={marcarEnviado} disabled={submitting}>
                  Marcar como enviado
                </button>
              </>
            )}

            {cuenta.estado !== 'verificada' && (
              <>
                <button
                  className="btn btn-primary"
                  onClick={verificar}
                  disabled={submitting}
                >
                  {submitting ? 'Verificando…' : 'Verificar cuenta'}
                </button>
                <p style={{ fontSize: 11, color: 'var(--muted-foreground)', margin: 0, lineHeight: 1.5 }}>
                  Cotejá la imagen de titularidad contra los datos declarados. Al verificar
                  se habilitan los retiros a esta cuenta.
                </p>
              </>
            )}

            {(cuenta.estado === 'verificacion_enviada' || cuenta.estado === 'fallida') && (
              <button className="btn btn-ghost" onClick={reiniciar} disabled={submitting}>
                Reiniciar verificación
              </button>
            )}

            {puedeVerificarPorComprobante(cuenta.estado) && (
              <>
                <button
                  className="btn btn-primary"
                  onClick={onVerificarPorComprobante}
                  disabled={submitting}
                >
                  Verificar por comprobante
                </button>
                <p style={{ fontSize: 11, color: 'var(--muted-foreground)', margin: 0, lineHeight: 1.5 }}>
                  La transferencia con la que se asoció —o cualquier depósito hecho
                  desde su cuenta (depósito espejo de Q1)— prueba la titularidad.
                  Cotejá contra el estado de cuenta y usá esa referencia.
                </p>
              </>
            )}

            {cuenta.estado !== 'verificada' && cuenta.estado !== 'rechazada' && (
              <>
                <div style={{ marginTop: 16, paddingTop: 16, borderTop: '1px solid var(--hairline)' }}>
                  <label style={{ fontSize: 11, fontWeight: 800, letterSpacing: 1.2, color: 'var(--muted-foreground)', textTransform: 'uppercase' }}>
                    O rechazar (motivo)
                  </label>
                  <input
                    type="text"
                    value={motivoRechazo}
                    onChange={(e) => setMotivoRechazo(e.target.value)}
                    placeholder="Ej. Datos inconsistentes con DPI"
                    style={{
                      marginTop: 6,
                      padding: '10px 12px', border: '1px solid var(--hairline)',
                      borderRadius: 8, fontSize: 13, fontFamily: 'inherit', width: '100%',
                    }}
                  />
                  <button
                    onClick={rechazar}
                    disabled={submitting || !motivoRechazo.trim()}
                    style={{
                      marginTop: 8, width: '100%',
                      padding: '10px 14px', borderRadius: 8,
                      background: 'transparent',
                      border: '1px solid var(--danger, #a32d2d)',
                      color: 'var(--danger, #a32d2d)',
                      fontSize: 13, fontWeight: 700, cursor: 'pointer',
                    }}
                  >
                    Rechazar cuenta
                  </button>
                </div>
              </>
            )}
          </div>
        </div>

        <div style={{ padding: '14px 24px', borderTop: '1px solid var(--hairline)' }}>
          <button className="btn btn-ghost" onClick={onClose} style={{ width: '100%' }}>
            Cerrar
          </button>
        </div>
      </aside>
    </>
  )
}

function MatchBadge({ nivel }: { nivel: MatchNivel }) {
  const b = MATCH_BADGE[nivel]
  return (
    <span style={{
      fontSize: 9, fontWeight: 800, padding: '2px 8px', borderRadius: 99,
      background: b.bg, color: b.color,
      textTransform: 'uppercase', letterSpacing: 0.5, whiteSpace: 'nowrap',
    }}>
      {b.label}
    </span>
  )
}

/** Panel de asistencia visual: compara lo declarado vs lo extraído por OCR. */
function OcrComparacion({ cuenta }: { cuenta: CuentaRow }) {
  const ocr = cuenta.ocr_extraido

  if (!ocr) {
    return (
      <div style={{
        border: '1px dashed var(--hairline)', borderRadius: 10,
        padding: '10px 14px', marginBottom: 12,
        fontSize: 11, color: 'var(--muted-foreground)',
      }}>
        Sin OCR (registro previo o imagen no procesada).
      </div>
    )
  }

  const filas: Array<{ campo: string; declarado: string; ocr: string; nivel: MatchNivel; mono?: boolean }> = [
    {
      campo: 'Número',
      declarado: cuenta.numero_cuenta,
      ocr: ocr.numero_cuenta ?? '—',
      nivel: matchNumero(cuenta.numero_cuenta, ocr.numero_cuenta),
      mono: true,
    },
    {
      campo: 'Tipo',
      declarado: cuenta.tipo_cuenta,
      ocr: ocr.tipo_cuenta ?? '—',
      nivel: matchTipo(cuenta.tipo_cuenta, ocr.tipo_cuenta),
    },
    {
      campo: 'Titular',
      declarado: cuenta.titular_nombre,
      ocr: ocr.titular_nombre ?? '—',
      nivel: matchTitular(cuenta.titular_nombre, ocr.titular_nombre),
    },
    {
      campo: 'Banco',
      declarado: cuenta.banco,
      ocr: ocr.banco ?? '—',
      nivel: matchBanco(cuenta.banco, ocr.banco),
    },
  ]

  const confianza = ocr.confianza && CONFIANZA_BADGE[ocr.confianza] ? CONFIANZA_BADGE[ocr.confianza] : null

  return (
    <div style={{
      background: 'var(--bg)', border: '1px solid var(--hairline)',
      borderRadius: 10, padding: '10px 14px', marginBottom: 12,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, marginBottom: 8 }}>
        <span style={{ fontSize: 11, fontWeight: 800, color: 'var(--muted-foreground)', textTransform: 'uppercase', letterSpacing: 1.2 }}>
          OCR de la imagen
        </span>
        {confianza && (
          <span style={{
            fontSize: 9, fontWeight: 800, padding: '2px 8px', borderRadius: 99,
            background: confianza.bg, color: confianza.color,
            textTransform: 'uppercase', letterSpacing: 0.5, whiteSpace: 'nowrap',
          }}>
            {confianza.label}
          </span>
        )}
      </div>

      <div style={{
        display: 'grid', gridTemplateColumns: '64px 1fr 1fr auto',
        gap: '2px 10px', alignItems: 'center',
      }}>
        <HeadCell>Campo</HeadCell>
        <HeadCell>Declarado</HeadCell>
        <HeadCell>OCR</HeadCell>
        <HeadCell>&nbsp;</HeadCell>
        {filas.map((f) => (
          <div key={f.campo} style={{ display: 'contents' }}>
            <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--ink-soft)', padding: '6px 0' }}>{f.campo}</span>
            <span style={{
              fontSize: 12, fontWeight: 600, color: 'var(--ink)', padding: '6px 0', overflowWrap: 'anywhere',
              fontFamily: f.mono ? 'IBM Plex Mono, monospace' : undefined,
              fontFeatureSettings: f.mono ? '"tnum"' : undefined,
            }}>{f.declarado || '—'}</span>
            <span style={{
              fontSize: 12, fontWeight: 600, color: 'var(--ink-soft)', padding: '6px 0', overflowWrap: 'anywhere',
              fontFamily: f.mono ? 'IBM Plex Mono, monospace' : undefined,
              fontFeatureSettings: f.mono ? '"tnum"' : undefined,
            }}>{f.ocr}</span>
            <span style={{ justifySelf: 'end', padding: '6px 0' }}><MatchBadge nivel={f.nivel} /></span>
          </div>
        ))}
      </div>

      <p style={{ margin: '8px 0 0', fontSize: 10, color: 'var(--muted-foreground)', lineHeight: 1.5 }}>
        El OCR es solo asistencia visual. La decisión de verificar o rechazar es del operador.
      </p>
    </div>
  )
}

function HeadCell({ children }: { children: React.ReactNode }) {
  return (
    <span style={{
      fontSize: 9, fontWeight: 800, color: 'var(--muted-foreground)',
      textTransform: 'uppercase', letterSpacing: 0.8,
    }}>
      {children}
    </span>
  )
}

function Info({ label, value, mono, highlight }: { label: string; value: string; mono?: boolean; highlight?: boolean }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', gap: 16 }}>
      <span style={{ fontSize: 11, color: 'var(--muted-foreground)', textTransform: 'uppercase', letterSpacing: 1.2, fontWeight: 700 }}>
        {label}
      </span>
      <span style={{
        fontSize: 13, fontWeight: 600,
        color: highlight ? 'var(--brand-700)' : 'var(--ink)',
        fontFamily: mono ? 'IBM Plex Mono, monospace' : undefined,
        fontFeatureSettings: mono ? '"tnum"' : undefined,
        textAlign: 'right',
      }}>
        {value}
      </span>
    </div>
  )
}

// Icons used in optional empty/badges
void CheckCircle; void Hourglass; void Warning; void XCircle
